/* eslint-disable @typescript-eslint/no-explicit-any */
import useCompareProperty from '@/hooks/useCompareProperty';
import { Image, X } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { FaCheck, FaPen } from 'react-icons/fa6';
import ButtonTooltip from './ButtonTooltip';
import type { IComparableProperty } from './ComparableSales';
import Gallery from './Gallery';
import { Button } from './ui/button';
import { Card, CardContent, CardFooter, CardHeader } from './ui/card';
import { CarouselItem } from './ui/carousel';
import { Slider } from './ui/slider';

interface Props {
  comp: IComparableProperty;
  propertyCorrections?: {
    livingAreaSqft?: number;
    buildingSqft?: number;
    garageSize?: string;
  };
  subjectPropertySqft?: number;
  adjustmentRatePerSqft: number;
  index: number;
  totalItems: number;
  onDiscardComp: (id: string) => void;
  onAdjustPrice: (value: number) => void;
  onNote: (notes: string) => void;
  onAdjustField: (field: string, value: any) => void;
}

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('en-US').format(num);
};
const formatPrice = (price: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(price);
};

// Function to format address for Zillow URL
const formatAddressForZillow = (address: string) => {
  // Replace spaces with dashes and remove any special characters except dashes
  return address.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '');
};

// Function to open Zillow link
const openZillowLink = (address: string) => {
  const formattedAddress = formatAddressForZillow(address);
  const zillowUrl = `https://www.zillow.com/homes/${formattedAddress}_rb/`;
  window.open(zillowUrl, '_blank');
};

const ComparableProperty: FC<Props> = ({
  comp,
  propertyCorrections,
  subjectPropertySqft,
  adjustmentRatePerSqft,
  index,
  totalItems,
  onDiscardComp,
  onAdjustPrice,
  onNote,
  onAdjustField,
}) => {
  const [conditionValues, setConditionValues] = useState<
    Record<string, number>
  >({});
  const [locationValues, setLocationValues] = useState<Record<string, number>>(
    {},
  );
  const [garageValues, setGarageValues] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState('');
  const [initialIndex, setInitialIndex] = useState(0);
  const [openGallery, setOpenGallery] = useState(false);
  const [openComparison, setOpenComparison] = useState(false);
  const [soldPrice, setSoldPrice] = useState(
    comp.soldPrice ?? comp.mls.ClosePrice,
  );
  const [isEditSoldPrice, setIsEditSoldPrice] = useState(false);

  // New state for the fourth slider (other features)
  const [otherFeaturesValues, setOtherFeaturesValues] = useState<
    Record<string, number>
  >({});

  // New state for date of sale adjustment slider
  const [dateOfSaleValues, setDateOfSaleValues] = useState<
    Record<string, number>
  >({});

  const {
    mutate: compare,
    isPending: compareLoading,
    data: aiResponse,
  } = useCompareProperty();

  // Calculate condition adjustment based on slider value and sold price
  const calculateConditionAdjustment = (
    sliderValue: number,
    soldPrice: number,
  ) => {
    // Slider range is 0-100, with 50 being "same" (0% adjustment)
    // 0 = much worse (+10%), 50 = same (0%), 100 = much better (-10%)
    const adjustmentPercent = ((sliderValue - 50) / 50) * -0.1; // -10% to +10%
    return soldPrice * adjustmentPercent;
  };

  // Calculate location adjustment based on slider value and sold price
  const calculateLocationAdjustment = (
    sliderValue: number,
    soldPrice: number,
  ) => {
    // Same calculation logic as condition
    const adjustmentPercent = ((sliderValue - 50) / 50) * -0.1; // -10% to +10%
    return soldPrice * adjustmentPercent;
  };

  // Calculate garage adjustment based on slider value and sold price
  const calculateGarageAdjustment = (
    sliderValue: number,
    soldPrice: number,
  ) => {
    // Same calculation logic as condition and location
    const adjustmentPercent = ((sliderValue - 50) / 50) * -0.1; // -10% to +10%
    return soldPrice * adjustmentPercent;
  };

  // Calculate other features adjustment based on slider value and sold price
  const calculateOtherFeaturesAdjustment = (
    sliderValue: number,
    soldPrice: number,
  ) => {
    // Same calculation logic as other sliders
    const adjustmentPercent = ((sliderValue - 50) / 50) * -0.1; // -10% to +10%
    return soldPrice * adjustmentPercent;
  };

  // Calculate date of sale adjustment based on slider value and sold price
  const calculateDateOfSaleAdjustment = (
    sliderValue: number,
    soldPrice: number,
  ) => {
    // Slider range is 0-100, with 50 being "same" (0% adjustment)
    // 0 = prices have increased (+10%), 50 = same (0%), 100 = prices have decreased (-10%)
    const adjustmentPercent = ((50 - sliderValue) / 50) * 0.1; // +10% to -10%
    return soldPrice * adjustmentPercent;
  };

  // Calculate living area adjustment based on subject property vs comparable
  const calculateLivingAreaAdjustment = (compSqft: number) => {
    // Use corrected living area if available, otherwise use original
    const effectiveSubjectSqft =
      propertyCorrections?.livingAreaSqft || subjectPropertySqft;

    if (!effectiveSubjectSqft || !compSqft || !adjustmentRatePerSqft) {
      return 0;
    }

    // Calculate the difference in sqft (subject - comparable)
    const sqftDifference = effectiveSubjectSqft - compSqft;

    // If the difference is less than 50 sqft, don't make any adjustment
    if (Math.abs(sqftDifference) < 50) {
      console.log('Difference less than 50 sqft, no adjustment');
      return 0;
    }

    // Apply the adjustment rate per sqft
    const adjustment = sqftDifference * adjustmentRatePerSqft;
    console.log('Living area adjustment calculated:', adjustment);
    return adjustment;
  };

  const conditionValue = conditionValues[comp.id] || 50;
  const locationValue = locationValues[comp.id] || 50;
  const garageValue = garageValues[comp.id] || 50;
  const otherFeaturesValue = otherFeaturesValues[comp.id] || 50;
  const dateOfSaleValue = dateOfSaleValues[comp.id] || 50;

  const conditionAdjustment = calculateConditionAdjustment(
    conditionValue,
    comp.mls?.ClosePrice || soldPrice,
  );
  const locationAdjustment = calculateLocationAdjustment(
    locationValue,
    comp.mls?.ClosePrice || soldPrice,
  );
  const garageAdjustment = calculateGarageAdjustment(
    garageValue,
    comp.mls?.ClosePrice || soldPrice,
  );
  const otherFeaturesAdjustment = calculateOtherFeaturesAdjustment(
    otherFeaturesValue,
    comp.mls?.ClosePrice || soldPrice,
  );
  const dateOfSaleAdjustment = calculateDateOfSaleAdjustment(
    dateOfSaleValue,
    comp.mls?.ClosePrice || soldPrice,
  );
  const livingAreaAdjustment = calculateLivingAreaAdjustment(
    // comp.mls?.LivingArea || comp.livingArea,
    comp.livingArea,
  );

  const handleConditionChange = (compId: string, value: number[]) => {
    onAdjustField(
      'conditionAdjustment',
      calculateConditionAdjustment(value[0], comp.mls?.ClosePrice || soldPrice),
    );
    setConditionValues((prev) => ({
      ...prev,
      [compId]: value[0],
    }));
  };

  // Handle location slider change
  const handleLocationChange = (compId: string, value: number[]) => {
    onAdjustField(
      'locationAdjustment',
      calculateLocationAdjustment(
        value[0],
        comp.mls?.ClosePrice || comp.soldPrice,
      ),
    );
    setLocationValues((prev) => ({
      ...prev,
      [compId]: value[0],
    }));
  };

  // Handle garage slider change
  const handleGarageChange = (compId: string, value: number[]) => {
    onAdjustField(
      'garageAdjustment',
      calculateGarageAdjustment(value[0], comp.mls?.ClosePrice || soldPrice),
    );
    setGarageValues((prev) => ({
      ...prev,
      [compId]: value[0],
    }));
  };

  // Handle other features slider change
  const handleOtherFeaturesChange = (compId: string, value: number[]) => {
    onAdjustField(
      'otherFeaturesAdjustment',
      calculateOtherFeaturesAdjustment(
        value[0],
        comp.mls?.ClosePrice || soldPrice,
      ),
    );
    setOtherFeaturesValues((prev) => ({
      ...prev,
      [compId]: value[0],
    }));
  };

  // Handle date of sale slider change
  const handleDateOfSaleChange = (compId: string, value: number[]) => {
    onAdjustField(
      'dateAdjustment',
      calculateDateOfSaleAdjustment(
        value[0],
        comp.mls?.ClosePrice || soldPrice,
      ),
    );
    setDateOfSaleValues((prev) => ({
      ...prev,
      [compId]: value[0],
    }));
  };

  useEffect(() => {
    if (livingAreaAdjustment) {
      onAdjustField('livingAreaAdjustment', livingAreaAdjustment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livingAreaAdjustment]);

  // Calculate adjusted value
  const adjustedValue =
    soldPrice +
    conditionAdjustment +
    locationAdjustment +
    garageAdjustment +
    otherFeaturesAdjustment +
    dateOfSaleAdjustment +
    livingAreaAdjustment;

  useEffect(() => {
    onNote(notes);
  }, [notes]);

  useEffect(() => {
    if (adjustedValue !== soldPrice) {
      onAdjustPrice(adjustedValue);
    } else {
      onAdjustPrice(0);
    }
  }, [adjustedValue, soldPrice]);

  // Calculate cost per sqft for this comparable
  const costPerSqft = comp.livingArea > 0 ? soldPrice / comp.livingArea : 0;

  const handleCompare = (subjectImg: string, comparableImg: string) => {
    compare(
      {
        address: comp.address,
        subjectImg,
        comparableImg,
        soldPrice: formatPrice(soldPrice),
        yearBuilt: comp.mls?.YearBuilt,
      },
      {
        onSuccess: () => {
          setOpenComparison(true);
        },
      },
    );
  };

  return (
    <CarouselItem key={comp.id}>
      <Card className="border border-tax-borderGray">
        <CardHeader className="p-4 border-b gap-2 border-tax-borderGray bg-tax-lightGray flex flex-col lg:flex-row justify-between items-center">
          <div className="flex gap-2 shrink-0 w-[150px]" />

          <div className="flex-1">
            <div className="text-sm text-tax-neutralGray">
              Comp {index + 1} of {totalItems}
            </div>
            <div className="font-medium text-lg truncate max-w-[calc(100vw-233px)] md:max-w-full">
              {comp.address}
            </div>
          </div>
          <div className="flex gap-2 shrink-0 w-[143px]">
            <Button
              variant="outline"
              size="sm"
              className="bg-red-100 border-red-500 text-red-700 hover:bg-red-200 w-full"
              onClick={() => onDiscardComp(comp.id)}
            >
              <X className="h-4 w-4 mr-1" /> Discard
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-sm text-tax-neutralGray">Sold Price</div>
              <div className="flex items-center justify-center gap-2">
                {isEditSoldPrice ? (
                  <input
                    className="w-[110px] font-bold text-lg focus:outline-none border-none"
                    value={soldPrice}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === '' || /^[0-9\b]+$/.test(value)) {
                        setSoldPrice(Number(value));

                        onAdjustField('soldPrice', Number(value));
                      }
                    }}
                  />
                ) : (
                  <p className="font-bold text-lg">{formatPrice(soldPrice)}</p>
                )}
                {soldPrice === 0 && (
                  <button onClick={() => setIsEditSoldPrice((prev) => !prev)}>
                    {isEditSoldPrice ? <FaCheck /> : <FaPen />}
                  </button>
                )}
              </div>
            </div>
            <div>
              <div className="text-sm text-tax-neutralGray">Sold Date</div>
              <div className="font-medium">
                {new Date(comp.soldDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </div>
            </div>
            <div>
              <div className="text-sm text-tax-neutralGray">Living Area</div>
              <div className="font-medium">
                {formatNumber(comp.livingArea)} sq ft
              </div>
            </div>
            <div>
              <div className="text-sm text-tax-neutralGray">Cost Per SQFT</div>
              <div className="font-medium">{formatPrice(costPerSqft)}</div>
            </div>
            <div className="col-span-2">
              <button
                onClick={() => openZillowLink(comp.address)}
                className="flex items-center gap-2 text-tax-brightBlue hover:text-tax-brightBlue/80 text-sm transition-colors"
              >
                <Image className="h-4 w-4" />
                <span>View photos and details on Zillow</span>
              </button>
            </div>
          </div>

          {!!comp.mls?.Media?.length && (
            <div className="flex gap-2 w-full overflow-x-auto mb-4">
              {comp.mls?.Media.map((media, index) => (
                <img
                  key={index}
                  src={media.MediaURL}
                  className="aspect-auto cursor-pointer max-h-[120px]"
                  onClick={() => {
                    setInitialIndex(index);
                    setOpenGallery(true);
                  }}
                />
              ))}
              <Gallery
                images={comp.mls?.Media.map((i) => i.MediaURL)}
                initialIndex={initialIndex}
                isOpen={openGallery}
                setIsOpen={setOpenGallery}
                onCompare={handleCompare}
              />
            </div>
          )}

          {/* {!!comparisons?.length && (
            <div className="mb-4">
              <h4 className="font-medium mb-2 text-tax-deepBlue border-b pb-1">
                Comparisons
              </h4>
              <div className="grid grid-cols-4 gap-3">
                {comparisons.map((c) => (
                  <div className="rounded-lg border border-tax-borderGray p-3">
                    <p className="text-sm truncate"> {c.PropertyB.address}</p>
                  </div>
                ))}
              </div>
            </div>
          )} */}

          {/* Size & Layout Section */}
          <div className="mb-4">
            <h4 className="font-medium mb-2 text-tax-deepBlue border-b pb-1">
              Size & Layout
            </h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <div className="flex items-center justify-center gap-2">
                  <div className="text-sm text-tax-neutralGray">Bedrooms</div>
                  <ButtonTooltip
                    message={
                      comp.mls
                        ? 'We are using MLS data'
                        : 'We are using public records data'
                    }
                  />
                </div>
                <div>{comp.mls?.BedroomsTotal || comp.bedrooms}</div>
              </div>
              <div>
                <div className="flex items-center justify-center gap-2">
                  <div className="text-sm text-tax-neutralGray">Bathrooms</div>
                  <ButtonTooltip
                    message={
                      comp.mls
                        ? 'We are using MLS data'
                        : 'We are using public records data'
                    }
                  />
                </div>
                <div>{comp.mls?.BathroomsTotalInteger || comp.bathrooms}</div>
              </div>
              <div>
                <div className="flex items-center justify-center gap-2">
                  <div className="text-sm text-tax-neutralGray">Lot Size</div>
                  <ButtonTooltip
                    message={
                      comp.mls
                        ? 'We are using MLS data'
                        : 'We are using public records data'
                    }
                  />
                </div>
                <div>
                  {comp.mls?.LotSizeArea
                    ? `${comp.mls?.LotSizeArea} sq ft`
                    : `${comp.lotSize} ${comp.lotUnit}`}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-center gap-2">
                  <div className="text-sm text-tax-neutralGray">Garage</div>
                  <ButtonTooltip
                    message={
                      comp.mls
                        ? 'We are using MLS data'
                        : 'We are using public records data'
                    }
                  />
                </div>
                <div>{comp.mls?.GarageSpaces || comp.garageType}</div>
              </div>
            </div>
          </div>

          {/* Area Breakdown Section */}
          {comp.mls && (
            <div className="mb-4">
              <h4 className="font-medium mb-2 text-tax-deepBlue border-b pb-1">
                Area Breakdown
              </h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <div>
                  <div className="text-sm text-tax-neutralGray">
                    Lot Size Area
                  </div>
                  <div>
                    {comp.mls?.LotSizeArea
                      ? `${comp.mls?.LotSizeArea} sq ft`
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-tax-neutralGray">
                    Total Finished Area
                  </div>
                  <div>
                    {comp.mls?.LivingArea
                      ? `${comp.mls?.LivingArea} sq ft`
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-tax-neutralGray">
                    Above Grade Finished Area
                  </div>
                  <div>
                    {comp.mls?.AboveGradeFinishedArea
                      ? `${comp.mls?.AboveGradeFinishedArea} sq ft`
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-tax-neutralGray">
                    Below Grade Finished Area
                  </div>
                  <div>
                    {comp.mls?.BelowGradeFinishedArea
                      ? `${comp.mls?.BelowGradeFinishedArea} sq ft`
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-tax-neutralGray">Basement</div>
                  <div>
                    {comp.mls?.Basement?.length
                      ? comp.mls?.Basement?.join(', ')
                      : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Structure & Features Section */}
          <div className="mb-4">
            <h4 className="font-medium mb-2 text-tax-deepBlue border-b pb-1">
              Structure & Features
            </h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <div className="flex items-center justify-center gap-2">
                  <div className="text-sm text-tax-neutralGray">Year Built</div>
                  <ButtonTooltip
                    message={
                      comp.mls
                        ? 'We are using MLS data'
                        : 'We are using public records data'
                    }
                  />
                </div>
                <div>{comp.mls?.YearBuilt || comp.yearBuilt}</div>
              </div>
              <div>
                <div className="flex items-center justify-center gap-2">
                  <div className="text-sm text-tax-neutralGray">Basement</div>
                  <ButtonTooltip
                    message={
                      comp.mls
                        ? 'We are using MLS data'
                        : 'We are using public records data'
                    }
                  />
                </div>
                <div>
                  {typeof comp.mls?.BasementYN !== 'undefined'
                    ? comp.mls?.BasementYN
                      ? 'Yes'
                      : 'No'
                    : comp.basement}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-center gap-2">
                  <div className="text-sm text-tax-neutralGray">
                    Construction
                  </div>
                  <ButtonTooltip
                    message={
                      comp.mls
                        ? 'We are using MLS data'
                        : 'We are using public records data'
                    }
                  />
                </div>
                <div>
                  {comp.mls?.ConstructionMaterials?.length
                    ? comp.mls?.ConstructionMaterials.join(', ')
                    : comp.constructionType}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-center gap-2">
                  <div className="text-sm text-tax-neutralGray">HVAC</div>
                  <ButtonTooltip
                    message={
                      comp.mls
                        ? 'We are using MLS data'
                        : 'We are using public records data'
                    }
                  />
                </div>
                <div>
                  {comp.mls?.Cooling?.length
                    ? comp.mls?.Cooling.join(', ')
                    : comp.hvac}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-center gap-2">
                  <div className="text-sm text-tax-neutralGray">Roof</div>
                  <ButtonTooltip
                    message={
                      comp.mls
                        ? 'We are using MLS data'
                        : 'We are using public records data'
                    }
                  />
                </div>
                <div>
                  {comp.mls?.Roof?.length
                    ? comp.mls?.Roof.join(', ')
                    : comp.roofMaterial}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-center gap-2">
                  <div className="text-sm text-tax-neutralGray">Pool</div>
                  <ButtonTooltip
                    message={
                      comp.mls
                        ? 'We are using MLS data'
                        : 'We are using public records data'
                    }
                  />
                </div>
                <div>
                  {typeof comp.mls?.PoolPrivateYN !== 'undefined'
                    ? comp.mls?.PoolPrivateYN
                      ? 'Yes'
                      : 'No'
                    : comp.pool
                      ? 'Yes'
                      : 'No'}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex-col p-4 pt-0 border-t mt-4">
          <div className="w-full">
            <h4 className="font-medium mb-2 text-tax-deepBlue">
              How Does This Home Compare To Your Home?
            </h4>

            <div className="mb-4">
              <div className="flex justify-between mb-1">
                <span className="text-sm">Condition</span>
              </div>
              <div
                className="flex items-center gap-3"
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
              >
                <div className="flex-1">
                  <Slider
                    value={[conditionValue]}
                    onValueChange={(value) =>
                      handleConditionChange(comp.id, value)
                    }
                    max={100}
                    step={1}
                    className="mb-1"
                  />
                  <div className="flex justify-between text-xs text-tax-neutralGray">
                    <span>Much worse</span>
                    <span>Same</span>
                    <span>Much better</span>
                  </div>
                </div>
                <div className="min-w-[80px] text-right">
                  <div
                    className={`font-bold text-sm ${
                      conditionAdjustment > 0
                        ? 'text-green-600'
                        : conditionAdjustment < 0
                          ? 'text-red-600'
                          : 'text-gray-600'
                    }`}
                  >
                    {conditionAdjustment > 0 ? '+' : ''}
                    {formatPrice(conditionAdjustment)}
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex justify-between mb-1">
                <span className="text-sm">Lot, Views & Location</span>
              </div>
              <div
                className="flex items-center gap-3"
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
              >
                <div className="flex-1">
                  <Slider
                    value={[locationValue]}
                    onValueChange={(value) =>
                      handleLocationChange(comp.id, value)
                    }
                    max={100}
                    step={1}
                    className="mb-1"
                  />
                  <div className="flex justify-between text-xs text-tax-neutralGray">
                    <span>Much worse</span>
                    <span>Same</span>
                    <span>Much better</span>
                  </div>
                </div>
                <div className="min-w-[80px] text-right">
                  <div
                    className={`font-bold text-sm ${
                      locationAdjustment > 0
                        ? 'text-green-600'
                        : locationAdjustment < 0
                          ? 'text-red-600'
                          : 'text-gray-600'
                    }`}
                  >
                    {locationAdjustment > 0 ? '+' : ''}
                    {formatPrice(locationAdjustment)}
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex justify-between mb-1">
                <span className="text-sm">
                  Garage space, outbuildings and accessory structures
                </span>
              </div>
              <div
                className="flex items-center gap-3"
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
              >
                <div className="flex-1">
                  <Slider
                    value={[garageValue]}
                    onValueChange={(value) =>
                      handleGarageChange(comp.id, value)
                    }
                    max={100}
                    step={1}
                    className="mb-1"
                  />
                  <div className="flex justify-between text-xs text-tax-neutralGray">
                    <span>Much worse</span>
                    <span>Same</span>
                    <span>Much better</span>
                  </div>
                </div>
                <div className="min-w-[80px] text-right">
                  <div
                    className={`font-bold text-sm ${
                      garageAdjustment > 0
                        ? 'text-green-600'
                        : garageAdjustment < 0
                          ? 'text-red-600'
                          : 'text-gray-600'
                    }`}
                  >
                    {garageAdjustment > 0 ? '+' : ''}
                    {formatPrice(garageAdjustment)}
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4 mt-4">
              <div className="flex justify-between mb-1">
                <span className="text-sm">
                  Date Of Sale Adjustment: Have real estate prices gone up or
                  down since this property sold
                </span>
              </div>
              <div
                className="flex items-center gap-3"
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
              >
                <div className="flex-1">
                  <Slider
                    value={[dateOfSaleValue]}
                    onValueChange={(value) =>
                      handleDateOfSaleChange(comp.id, value)
                    }
                    max={100}
                    step={1}
                    className="mb-1"
                  />
                  <div className="flex justify-between text-xs text-tax-neutralGray">
                    <span>Prices have increased</span>
                    <span>Same</span>
                    <span>Prices have decreased</span>
                  </div>
                </div>
                <div className="min-w-[80px] text-right">
                  <div
                    className={`font-bold text-sm ${
                      dateOfSaleAdjustment > 0
                        ? 'text-green-600'
                        : dateOfSaleAdjustment < 0
                          ? 'text-red-600'
                          : 'text-gray-600'
                    }`}
                  >
                    {dateOfSaleAdjustment > 0 ? '+' : ''}
                    {formatPrice(dateOfSaleAdjustment)}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-sm mb-1 block">
                Tell us about other features and how this home compares to
                yours:
              </label>
              <textarea
                className="w-full p-2 border border-tax-borderGray rounded-md"
                rows={2}
                placeholder="Optional—view, layout, finishes, etc."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              ></textarea>
              <div
                className="flex items-center gap-3 mt-4"
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
              >
                <div className="flex-1">
                  <Slider
                    value={[otherFeaturesValue]}
                    onValueChange={(value) =>
                      handleOtherFeaturesChange(comp.id, value)
                    }
                    max={100}
                    step={1}
                    className="mb-1"
                  />
                  <div className="flex justify-between text-xs text-tax-neutralGray">
                    <span>Much worse</span>
                    <span>Same</span>
                    <span>Much better</span>
                  </div>
                </div>
                <div className="min-w-[80px] text-right">
                  <div
                    className={`font-bold text-sm ${
                      otherFeaturesAdjustment > 0
                        ? 'text-green-600'
                        : otherFeaturesAdjustment < 0
                          ? 'text-red-600'
                          : 'text-gray-600'
                    }`}
                  >
                    {otherFeaturesAdjustment > 0 ? '+' : ''}
                    {formatPrice(otherFeaturesAdjustment)}
                  </div>
                </div>
              </div>
            </div>

            {/* New Adjusted Value Analysis Section */}
            <div className="mt-6 p-4 bg-tax-lightGray rounded-lg border border-tax-borderGray">
              <h5 className="font-medium mb-3 text-tax-deepBlue">
                Adjusted Value Analysis
              </h5>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Sold Price:</span>
                  <span className="font-medium">{formatPrice(soldPrice)}</span>
                </div>

                <div className="flex justify-between">
                  <span>Condition Adjustment:</span>
                  <span
                    className={`font-medium ${
                      conditionAdjustment > 0
                        ? 'text-green-600'
                        : conditionAdjustment < 0
                          ? 'text-red-600'
                          : 'text-gray-600'
                    }`}
                  >
                    {conditionAdjustment > 0 ? '+' : ''}
                    {formatPrice(conditionAdjustment)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Lot/View/Location Adjustment:</span>
                  <span
                    className={`font-medium ${
                      locationAdjustment > 0
                        ? 'text-green-600'
                        : locationAdjustment < 0
                          ? 'text-red-600'
                          : 'text-gray-600'
                    }`}
                  >
                    {locationAdjustment > 0 ? '+' : ''}
                    {formatPrice(locationAdjustment)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Garage/Outbuildings Adjustment:</span>
                  <span
                    className={`font-medium ${
                      garageAdjustment > 0
                        ? 'text-green-600'
                        : garageAdjustment < 0
                          ? 'text-red-600'
                          : 'text-gray-600'
                    }`}
                  >
                    {garageAdjustment > 0 ? '+' : ''}
                    {formatPrice(garageAdjustment)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Date Of Sale Adjustment:</span>
                  <span
                    className={`font-medium ${
                      dateOfSaleAdjustment > 0
                        ? 'text-green-600'
                        : dateOfSaleAdjustment < 0
                          ? 'text-red-600'
                          : 'text-gray-600'
                    }`}
                  >
                    {dateOfSaleAdjustment > 0 ? '+' : ''}
                    {formatPrice(dateOfSaleAdjustment)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Other Features Adjustment:</span>
                  <span
                    className={`font-medium ${
                      otherFeaturesAdjustment > 0
                        ? 'text-green-600'
                        : otherFeaturesAdjustment < 0
                          ? 'text-red-600'
                          : 'text-gray-600'
                    }`}
                  >
                    {otherFeaturesAdjustment > 0 ? '+' : ''}
                    {formatPrice(otherFeaturesAdjustment)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Living Area Adjustment:</span>
                  <span
                    className={`font-medium ${
                      livingAreaAdjustment > 0
                        ? 'text-green-600'
                        : livingAreaAdjustment < 0
                          ? 'text-red-600'
                          : 'text-gray-600'
                    }`}
                  >
                    {livingAreaAdjustment > 0 ? '+' : ''}
                    {formatPrice(livingAreaAdjustment)}
                  </span>
                </div>

                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-base font-bold">
                    <span>Adjusted Value:</span>
                    <span className="text-tax-deepBlue">
                      {formatPrice(adjustedValue)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardFooter>
      </Card>
    </CarouselItem>
  );
};

export default ComparableProperty;
